import { useQuery } from '@tanstack/react-query'
import { useEffect, useState, useMemo } from 'react'
import { NO_EXECUTE_CONTENTFULL_ROUTES } from 'utils/utils'
import { queriesApi } from '../services/queriesService'

const DEFAULT_TIME = 120 * 60 * 1000 // 2 horas en milisegundos
const ROUTES_EXCEPTION = ['/', ...NO_EXECUTE_CONTENTFULL_ROUTES]

/**
 * Hook para manejar el timeout de sesión de usuario
 * Utiliza Tanstack Query para cache de 5 días y mejores prácticas
 *
 * @returns {Object} Funciones y configuración para el idle timer
 */
export const useIdleTimer = () => {
	// Verificar si estamos en una ruta de excepción al inicio del hook
	const pathname = window.location.pathname
	const isExceptionRoute = ROUTES_EXCEPTION.includes(pathname)

	const [timeoutValue, setTimeoutValue] = useState(DEFAULT_TIME)

	// Query con Tanstack Query - solo se ejecuta si no estamos en ruta de excepción
	const {
		data: rawData,
		isLoading,
		error,
		refetch,
		isStale,
		isRefetching
	} = useQuery({
		queryKey: ['contentful', 'timeLogout'] as const,
		queryFn: queriesApi.getTimeLogout,
		staleTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		cacheTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		retry: 2,
		retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
		refetchOnWindowFocus: false,
		refetchOnReconnect: true,
		enabled: !isExceptionRoute // Solo ejecutar si no estamos en ruta de excepción
	})

	// Manejar errores de manera moderna
	if (error) {
		console.error('Error al obtener configuración de timeout desde Contentful:', error)
	}

	// Función que se ejecuta cuando el usuario está inactivo
	const onIdle = () => {
		localStorage.clear()
		sessionStorage.clear()
		alert('Tu sesión ha finalizado. Ingresa nuevamente.')
		window.location.reload()
	}

	// Procesar timeout usando memoización para evitar recálculos innecesarios
	const processedTimeoutValue = useMemo(() => {
		if (!rawData || isLoading || error || isExceptionRoute) {
			return DEFAULT_TIME
		}

		try {
			const timeFormatHHmm = rawData?.contenidosRetos?.duracionDelReto ?? '02:00'
			const [hoursStr, minutesStr] = timeFormatHHmm.split(':')
			const hours = parseInt(hoursStr, 10)
			const minutes = parseInt(minutesStr, 10)
			const totalMilliseconds = hours * 60 * 60 * 1000 + minutes * 60 * 1000

			return totalMilliseconds || DEFAULT_TIME
		} catch (transformError) {
			console.error('Error al procesar configuración de timeout:', transformError)
			return DEFAULT_TIME
		}
	}, [rawData, isLoading, error, isExceptionRoute])

	// Actualizar el valor del timeout cuando cambie el valor procesado
	useEffect(() => {
		setTimeoutValue(processedTimeoutValue)
	}, [processedTimeoutValue])

	// Información adicional sobre el estado del cache
	const cacheInfo = useMemo(
		() => ({
			isStale,
			isRefetching,
			lastFetch: rawData ? new Date() : null,
			cacheExpiry: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000), // 5 días desde ahora
			isExceptionRoute,
			currentTimeoutMs: timeoutValue,
			currentTimeoutHours: Math.round((timeoutValue / (1000 * 60 * 60)) * 100) / 100
		}),
		[isStale, isRefetching, rawData, isExceptionRoute, timeoutValue]
	)

	// Gestión del timer de inactividad
	useEffect(() => {
		// Si estamos en una ruta de excepción, no ejecutar nada
		if (isExceptionRoute) return

		let timer: any
		const handleUserActivity = () => {
			clearTimeout(timer)
			timer = setTimeout(() => {
				onIdle()
			}, timeoutValue)
		}

		// Inicializar el timer
		handleUserActivity()

		// Eventos que indican actividad del usuario
		const activityEvents = ['mousemove', 'keydown', 'blur']
		activityEvents.forEach(eventType => {
			window.addEventListener(eventType, handleUserActivity)
		})

		// Cleanup
		return () => {
			clearTimeout(timer)
			activityEvents.forEach(eventType => {
				window.removeEventListener(eventType, handleUserActivity)
			})
		}
	}, [timeoutValue, isExceptionRoute])

	return {
		// Funcionalidades principales
		onIdle,
		timeoutValue,
		isExceptionRoute,

		// Estados de la query (para compatibilidad y debugging)
		loading: isLoading,
		error,

		// Funcionalidades adicionales de Tanstack Query
		refetch,
		isStale,
		isRefetching,
		cacheInfo,

		// Datos raw por si se necesitan
		rawData
	}
}
